import { ERROR_CODES, isErrorCode, type ApiError } from "@zapir/shared";

export type ErrorActionKind =
  | "reauth"
  | "back"
  | "open_pinned"
  | "open_existing_label"
  | "pick_other_assignee"
  | "open_channel"
  | "retry"
  | "focus_first_error"
  | "remove_mention"
  | "hide_feature"
  | "show_status"
  | "confirm_race"
  | "reload"
  | "ask_admin"
  | "none";

export interface ErrorCopy {
  code: string;
  status: number;
  copy: string;
  action: string;
  kind: ErrorActionKind;
}

const ACTION_KINDS: Record<keyof typeof ERROR_CODES, ErrorActionKind> = {
  UNAUTHENTICATED: "reauth",
  FORBIDDEN_ROLE: "ask_admin",
  ASSIGNEE_NOT_ALLOWED: "pick_other_assignee",
  NOT_FOUND: "back",
  ASSIGN_RACE: "confirm_race",
  INVALID_TRANSITION: "show_status",
  LABEL_NAME_CONFLICT: "open_existing_label",
  CHANNEL_DISCONNECTED: "open_channel",
  GONE: "reload",
  VALIDATION: "focus_first_error",
  MENTION_OUT_OF_SCOPE: "remove_mention",
  CHANNEL_CAPABILITY_UNSUPPORTED: "hide_feature",
  PIN_LIMIT_REACHED: "open_pinned",
  RATE_LIMITED: "retry",
  SERVER_ERROR: "retry",
};

/** Erro de API tipado: carrega o `code` canonico e a copy PT-BR unica (§3.6). */
export class ZapirApiError extends Error {
  readonly code: ErrorCodeName;
  readonly status: number;
  readonly details: unknown;
  readonly copy: string;
  readonly action: string;
  readonly kind: ErrorActionKind;

  constructor(code: ErrorCodeName, status: number, details?: unknown) {
    const entry = ERROR_CODES[code];
    super(entry.copy);
    this.name = "ZapirApiError";
    this.code = code;
    this.status = status;
    this.details = details;
    this.copy = entry.copy;
    this.action = entry.action;
    this.kind = ACTION_KINDS[code];
  }
}

export type ErrorCodeName = keyof typeof ERROR_CODES;

/** Normaliza qualquer falha (rede, parse, body) para a taxonomia unica. */
export function toApiError(err: unknown): ZapirApiError {
  if (err instanceof ZapirApiError) return err;
  if (err instanceof DOMException && err.name === "AbortError") {
    return new ZapirApiError("SERVER_ERROR", 0, { aborted: true });
  }
  return new ZapirApiError("SERVER_ERROR", 0, err);
}

/**
 * Falha que JA e da nossa taxonomia, ou nada. Diferente de `toApiError`, que
 * embrulha qualquer coisa: aqui um erro estranho vira `null` de proposito,
 * para a tela decidir se mostra banner ou ignora.
 */
export function asApiError(err: unknown): ZapirApiError | null {
  return err instanceof ZapirApiError ? err : null;
}

export function isNetworkError(err: unknown): boolean {
  return err instanceof ZapirApiError && err.status === 0;
}

export function parseApiError(payload: unknown, status: number): ZapirApiError {
  const parsed = payload as Partial<ApiError> | null;
  const rawCode = typeof parsed?.code === "string" ? parsed.code : "";
  const code: ErrorCodeName = isErrorCode(rawCode) ? rawCode : "SERVER_ERROR";
  return new ZapirApiError(code, status, parsed?.details);
}
